import { Action } from "redux";

import { AggregationFunction, ConditionParams } from "@multiweb/clients/dataReader";
import { LocalizableOption } from "@multiweb/reactCore/i18n";
import { Option, TypedObject } from "@multiweb/types/react";
import {
    AGGREGATION_FUNCTION_SELECTED,
    CLEAR_AGGREGATION_ROW,
    CLOSE_BUTTON_ACTIVATED,
    COLUMN_ADDED,
    COLUMN_DEFINITIONS_CHANGED,
    COLUMN_REMOVED,
    COLUMN_WIDTH_CHANGED,
    FILTER_CHANGED,
    INIT_AGGREGATION_ROW,
    NUMBER_OF_FROZEN_COLUMNS_CHANGED,
    ORDER_OF_COLUMNS_CHANGED,
    RELOAD_AGGREGATION_RESULTS,
    SET_AGGREGATION_RESULTS,
} from "./constants";

/**
 * Struktura stavu modulu aggregationRow.
 */
export interface IAggregationRowState {
    readonly aggregationRows: AggregationRows;
}

/**
 * Data agregačních řádků indexovaná podle jejich klíčů.
 */
export type AggregationRows = TypedObject<AggregationRow>;

/** Filtr pro omezení agregovaných záznamů. */
export type Filter = Readonly<{
    /** Příznak, zda se má agregace provádět i nad verzovanými záznamy. */
    readVersioned: boolean;
    /** Podmínka omezující agregovaná data. */
    condition: string;
    /** Parametry pro podmínku omezující agregovaná data. */
    conditionParams?: ConditionParams;
}>;

/**
 * Název třídy (entity), nebo null při počátečním stavu.
 */
export type NullableClassName = string | null;

/**
 * Struktura dat pro jeden agregační řádek.
 */
export type AggregationRow = Readonly<{
    /** Název třídy (entity), jejíž atributy se zobrazují v tabulce s agregačním řádkem. */
    className: NullableClassName;
    /** Počet sloupců se zmraženou pozicí pro horizontální scrollování. */
    numberOfFrozenColumns: number;
    /** Pole klíčů sloupců v pořadí, ve kterém se mají vykreslovat. */
    orderOfColumns: ReadonlyArray<ColumnKey>;
    /** Data sloupců. */
    columns: Columns;
    /** Filtr pro omezení agregovaných záznamů. */
    filter: Filter;
}>;

/**
 * Klíč pro jednoznačnou identifikaci agregačního řádku.
 */
export type AggregationRowKey = string;

/**
 * Klíč sloupce jednoznačně identifikuje sloupec v rámci agregačního řádku.
 */
export type ColumnKey = string;

/**
 * Data sloupců v agregačním řádku indexovaná klíčem sloupce.
 */
export type Columns = TypedObject<Column>;

/**
 * Agregační funkce, nebo null, pokud není žádná vybraná.
 */
export type NullableAggregationFunction = null | AggregationFunction;

/**
 * Výsledek agregační funkce. Aktuálně jako typ string, protože BigNumberProxy není serializovatelný objekt a tím pádem ho nelze ukládat v reduxu.
 */
export type AggregationResult = string | null;

/**
 * Typ sloupce.
 */
export enum ColumnType {
    /** Číselný sloupec, pro který je možné počítat agregační funkce */
    numeric = "numeric",
    /** Speciální typ sloupce obsahující kontrolní prvky. Agregační řádek pro takový sloupec vykresluje tlačítko pro zavření. */
    control = "control",
    /** Typ pro ostatní sloupce, které nemají v agregačním řádku žádnou funkcionalitu. */
    other = "other",
}

/**
 * Struktura dat pro jeden sloupec agregačního řádku.
 */
export type Column = Readonly<{
    key: ColumnKey;
    /** Šířka sloupce v pixelech. */
    width: number;
    /** Příznak, zda je sloupec číselný. Pouze číselné sloupce podporují agregační funkce. */
    columnType: ColumnType;
    /** Název atributu, který sloupec zobrazuje. */
    attributeName: string | null;
    /** Agregační funkce vybraná pro číselný sloupec. */
    aggregationFunction: NullableAggregationFunction;
    /** Výsledek vybrané agregační funkce. */
    aggregationResult: AggregationResult;
    /** Formát použitý při zobrazování výsledku agregační funkce. */
    format: string | null;
}>;

/**
 * Data pro definici sloupce v agregačním řádku.
 */
export type ColumnDefinition = Readonly<{
    key: ColumnKey;
    attributeName?: string;
    columnType: ColumnType;
    width: number;
    format?: string;
}>;

/**
 * Objekt obsahující naformátované výsledky agregačních funkcí pod klíči sloupců, ke kterým patří.
 */
export type FormattedAggregationResults = TypedObject<AggregationResult>;

/**
 * Objekt obsahující zvolené agregační funkce pod klíči sloupců, ke kterým patří.
 * Pokud není žádná vybraná, obsahuje null.
 */
export type ColumnAggregationFunctions = TypedObject<NullableAggregationFunction>;

/**
 * Parametry pro selectory získávající údaje o agregačním řádku.
 */
export type SelectorParams = {
    readonly aggregationRowKey: AggregationRowKey;
};

/**
 * Parametry pro selectory získávající údaje o sloupci agregačního řádku.
 */
export type ColumnSelectorParams = SelectorParams & {
    readonly columnKey: ColumnKey;
};

/**
 * Lokalizovatelná option pro výběr agregační funkce.
 */
export type AggregationFunctionLocalizableOption = LocalizableOption<AggregationFunction>;

/**
 * Lokalizovaná option pro výběr agregační funkce.
 */
export type AggregationFunctionOption = Option<AggregationFunction>;

export interface IAggregationRowMappedAction extends Action<string> {
    readonly meta: {
        readonly aggregationRowKey: AggregationRowKey;
    };
}

export class IAggregationRowMappedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance?.meta?.aggregationRowKey;
    }
}

export interface IInitAggregationRowAction extends IAggregationRowMappedAction {
    readonly payload: {
        readonly className: string;
        readonly columnDefinitions: ReadonlyArray<ColumnDefinition>;
        readonly orderOfColumns: ReadonlyArray<ColumnKey>;
        readonly numberOfFrozenColumns: number;
        readonly filter: Filter;
    };
}

export class IInitAggregationRowAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === INIT_AGGREGATION_ROW;
    }
}

export interface IClearAggregationRowAction extends IAggregationRowMappedAction {}

export class IClearAggregationRowAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CLEAR_AGGREGATION_ROW;
    }
}

export interface IAggregationFunctionSelectedAction extends IAggregationRowMappedAction {
    readonly payload: {
        readonly columnKey: ColumnKey;
        readonly aggregationFunction: AggregationFunction | undefined;
    };
}

export class IAggregationFunctionSelectedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === AGGREGATION_FUNCTION_SELECTED;
    }
}

export interface ISetAggregationResultsAction extends IAggregationRowMappedAction {
    readonly payload: {
        readonly results: FormattedAggregationResults;
    };
}

export class ISetAggregationResultsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_AGGREGATION_RESULTS;
    }
}

export interface IColumnWidthChangedAction extends IAggregationRowMappedAction {
    readonly payload: {
        readonly columnKey: ColumnKey;
        readonly width: number;
    };
}

export class IColumnWidthChangedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === COLUMN_WIDTH_CHANGED;
    }
}

export interface INumberOfFrozenColumnsChangedAction extends IAggregationRowMappedAction {
    readonly payload: {
        readonly numberOfFrozenColumns: number;
    };
}

export class INumberOfFrozenColumnsChangedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === NUMBER_OF_FROZEN_COLUMNS_CHANGED;
    }
}

export interface IOrderOfColumnsChangedAction extends IAggregationRowMappedAction {
    readonly payload: {
        readonly orderOfColumns: ReadonlyArray<ColumnKey>;
    };
}

export class IOrderOfColumnsChangedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === ORDER_OF_COLUMNS_CHANGED;
    }
}

export interface IColumnDefinitionsChangedAction extends IAggregationRowMappedAction {
    readonly payload: {
        readonly columnDefinitions: ReadonlyArray<ColumnDefinition>;
        readonly orderOfColumns: ReadonlyArray<ColumnKey>;
        readonly numberOfFrozenColumns: number;
    };
}

export class IColumnDefinitionsChangedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === COLUMN_DEFINITIONS_CHANGED;
    }
}

export interface IColumnAddedAction extends IAggregationRowMappedAction {
    readonly payload: {
        readonly column: ColumnDefinition;
    };
}

export class IColumnAddedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === COLUMN_ADDED;
    }
}

export interface IColumnRemovedAction extends IAggregationRowMappedAction {
    readonly payload: {
        readonly columnKey: ColumnKey;
    };
}

export class IColumnRemovedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === COLUMN_REMOVED;
    }
}

export interface IReloadAggregationResultsAction extends IAggregationRowMappedAction {}

export class IReloadAggregationResultsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === RELOAD_AGGREGATION_RESULTS;
    }
}

export interface ICloseButtonActivatedAction extends IAggregationRowMappedAction {}

export class ICloseButtonActivatedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CLOSE_BUTTON_ACTIVATED;
    }
}

export interface IFilterChangedAction extends IAggregationRowMappedAction {
    readonly payload: {
        readonly filter: Filter;
    };
}

export class IFilterChangedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === FILTER_CHANGED;
    }
}
